
/* ==========================================================================

Purpose: 
This file handles the logic of: 
    - add-to-project-list.liquid
    - add-to-project-button-liquid,
    - add-to-project-tool.liquid

Features:
    - Removes products from the "Project List" page after 7 days has passed 
    - Adds a product to the page. Contains data on id, sku, title, image, description, quantity, productUrl
    - Updates numerical counter when products are added to the page. 
    - Renders display of page depending on page stage (empty, project list overview)
        - Dynamically changes product data used in building product card(s)
    - Supports incrementing and decrementing of product quantity
    - Deletes products 
    - Supports persistence of data when users close the tab or navigate to different pages.
    - Generates a downloadable PDF file 
    - Notifies when a product is added (or already added) to the Project List

Helpers created:
    - setExpiry() → Stores a set expiration on products added to list
    - getExpiry() → Retrieves desired list 
    - checkStorage() → Creates/Loads array into list location
    - updateCounter() → Displays num of products added to the project list
    - convertToBase64() → Encodes url as printable string (text)
    - createMargins(): Used in PDF file (type: JSPDF)
    - setLineHeight(): Used in PDF file (type: JSPDF)
    - setFooterLineHeight(): Used in JSPDF file
    - createUpdate() → Displays a notification when a product is added to the list
    - createDownloadAnchor() → Creates link to download PDF file
    - openPreview() → Creates and designs preview window of PDF file
    - viewPDF() → Opens file in a new window
    - downloadPDF() → Downloads file
    - verifyLengthInput(), checkPhoneInput(), checkEmailInput() → Raises errors when invalid input is entered
    - getIncludedLed() 

Main Functions:
    - renderBoard() → Builds the layout of the project list page (when its empty or in use) 
        and builds the layout of product cards (when a product is added to the list)
    - generatePDF() → Builds a PDF. Contains information pulled from users' project list.

Note:
*Assumes user is not logged in.
- Renders at theme.liquid, Line 426

========================================================================== */

getExpiry('productExpiry');

// Stores a value in localStorage with an expiry time
// TTL (time to live) determines how long the value remains valid.
function setExpiry(key, value, ttl) {
    const now = new Date();
    const productObj = {
        value: value,
        expiry: now.getTime() + ttl,
    }
    localStorage.setItem(key, JSON.stringify(productObj));
}


/* Checks whether a stored value has expired.
    If the current time is past the stored expiry time, removes the value from local storage.
    Returns the stored value if it is still valid.
*/
function getExpiry(key) {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
        return null
    }

    const productObj = JSON.parse(itemStr);
    const now = new Date();
    if (now.getTime() > productObj.expiry) {
        localStorage.removeItem(key);
        localStorage.removeItem('projectItems');
        return null
    }
    return productObj.value;
}

/* Checks whether a project list exist in local stoage
    If no list exists, create an empty list
    Otherwise, convert stored project list into an array
    Returns the project list
*/
function checkStorage() {
    let projectItems; // Inititalises variable     
    const temp = localStorage.getItem('projectItems'); 

    if (temp == null) { 
        projectItems = []; // Creates a new empty array
    } else {
        projectItems = JSON.parse(temp); // Converts information into an array and loads it
    }
    return projectItems;
}

/* Takes project list as an argument
    Calculates the total quantity of products in the project list and displays the total on the project list button counter
    Hides the counter when the project list is empty
*/
function updateCounter(projectItems) {
    let numOfProducts = projectItems.reduce((total , item) => total + parseInt(item.variantQuantity), 0); 
    const myProjectBtn = document.querySelector('.project-button-counter'); 
    if (!myProjectBtn) return; 

    // Displays total quantity on project button counter using number of products found in project list 
    myProjectBtn.textContent = numOfProducts;

    if ( numOfProducts === 0) {
	    document.querySelector('.project-button-counter').style.display = 'none';
    } else {
        if (numOfProducts >= 1) {
            document.querySelector('.project-button-counter').style.display = 'block';
        }
    }
}

// Converts a data file/blob into Base64 data so it can be used when generating the PDF
// Returns a Promise because the file is read asynchronously
function convertToBase64(blob) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.readAsDataURL(blob);
    })
}

// Takes doc from generatePDF() as an argument and creates margins around the document's perimeter
// Keeps generated PDF content away from the edges of the page
function createMargins(doc) {
    const margin = 12.7; // 0.5 inch in mm 
    const pageHeight = doc.internal.pageSize.getHeight(); 
    const pageWidth = doc.internal.pageSize.getWidth();

    const top = margin;
    const bottom = pageHeight - margin;
    const left = margin;
    const right = pageWidth - margin;

    return {
        top, bottom, left, right
    }; 
}

// Creates evenly spaced Y positions for user details in the PDF
// Used by generatePDF() to position each line with equal spacing vertically.
function setLineHeight() {
    const rows = [];
    let y = 46; // Vertical starting position
    for (let i = 0; i < 3; i++) {
        y += 6;
        rows.push(y);
    }
    return rows;
}

// Creates evenly spaced Y positions for the contact details in the PDF
// Used by generatePDF() to position each line with equal spacing vertically. 
function setFooterLineHeight() {
    const rows = [];
    let y = 264; // Vertical starting position
    for (let i = 0; i < 4; i++) {
        y += 6;
        rows.push(y);
    }
    return rows;
}

/*
    Creates and displays a notification when a project-list action occurs
    Uses event type to style the notification and control length of animated progress
    Removes an existing notification before displaying a new one
*/
function createUpdate(message, eventType, duration) {

    // Notification types supported by the project list tool
    let output = {
        success: `<span class="update-display">product-notification</span>`,
        error: `<span class="update-display">product-notification</span>`,
        info: `<span class="update-display">product-notification</span>`,
    };

    // Creates the notification container and applies the event type as a CSS class
    let box = document.createElement("div");
    box.classList.add("update", `update-${eventType}`);
    box.innerHTML=`<div class="update-content-wrapper">
                        <div class="update-message">${message}</div>
                        <div class="update-progress"></div>
                    </div>`;
    duration = duration || 3000;
    let boxProgress = box.querySelector(".update-progress");
    boxProgress.style.animationDuration = `${duration/1000}s`;

    let isUpdated = document.body.querySelector(".update");
    if (isUpdated) {
        isUpdated.remove();
        boxProgress.style.animationDuration = "0s";
    } 

    document.body.appendChild(box);
}

/*  Handles adding a product to the project list when the Add to Project button is clicked
    If product already exists in project list, loads list and displays an error notification. 
    If product is new to project list, add product to local storage and display a success notification. 
    Refer to createUpdate() function to view notification types.
*/
if (document.querySelector('.add-to-project-list-btn')) {
    (function() {
        let addToProjButton = document.querySelector('.add-to-project-list-btn');  

        // Loads the current project list and updates the project button counter
        const projectItems = checkStorage(); // TO DO: Review code
        updateCounter(projectItems);
        
        addToProjButton.addEventListener("click", function(e) {
            // Creates the product data object using values stored on the Add to Project button
            const productObj = {  
                productId: e.currentTarget.dataset.id,
                productSku: e.currentTarget.dataset.sku,
                productTitle: e.currentTarget.dataset.title,
                productImage: e.currentTarget.dataset.selectedVariantImage ||  e.currentTarget.dataset.image,
                productDescription: e.currentTarget.dataset.description,
                productUrl: e.currentTarget.dataset.url,
                variantId: e.currentTarget.dataset.selectedVariantId,
                variantTitle: e.currentTarget.dataset.selectedVariant,
                variantSku: e.currentTarget.dataset.selectedVariantSku,
                variantLength: e.currentTarget.dataset.selectedLength,
                variantQuantity: e.currentTarget.dataset.selectedQuantity,
                ledDetails: e.currentTarget.dataset.ledDetails,
                hasLength: e.currentTarget.dataset.hasLength,
            }

            const displaySku = productObj.variantSku || productObj.productSku;

            // Checks whether the same product and variant are already in the project list
            const findProduct = projectItems.find(
                p => p.productId === e.currentTarget.dataset.id &&
                p.variantSku === e.currentTarget.dataset.selectedVariantSku
            );

            if (findProduct) {
                // If the product already exists, increase its quantity instead of creating a duplicate. 
                // And, display an 'error' notification
                findProduct.variantQuantity = parseInt(findProduct.variantQuantity) + parseInt(productObj.variantQuantity);
                
                localStorage.setItem('projectItems', JSON.stringify(projectItems));
                updateCounter(projectItems);
                createUpdate(`Product ${displaySku} has already been added.`, "error", 5000);
            } else {
                // Add the new product to the project list and save the updated list
                // And, display a 'success' notification
                projectItems.push(productObj);  
                localStorage.setItem('projectItems', JSON.stringify(projectItems));  
                updateCounter(projectItems);
                createUpdate(`Product ${displaySku} is added.`, "success", 5000);

                // Start the seven-day expiry period if one does not already exist
                if (!getExpiry('productExpiry')) {
                    setExpiry('productExpiry', true, 604800000); 
                }
            }
        });
    })();
}

/*  Displays the appropriate project list page when the list is empty or contains products.
    Creates a product card for each product saved to the list.
    Adds input handlers so edited length and quantity values are saved back to the project list.
*/
function renderBoard() {
    const temp = localStorage.getItem('projectItems'); 

    // Loads saved products from local storage, or creates a new list if none exists.
    const tempArr = temp ? JSON.parse(temp) : [];

    if (tempArr.length === 0) {
        // Displays  the empty project list state -- instructions to navigate project list page
        document.querySelector('.empty-state').style.display = 'flex';
        document.querySelector('.form-container').style.display = 'none';
        document.querySelector('.board').style.display = 'none';
        document.querySelector('.continue-btn').style.display = 'none';
        document.querySelector('.reset-list-btn').style.display = 'none';
        document.querySelector('.explore-btn').style.display = 'flex';
        document.querySelector('.download-file').style.display = 'none';
        document.querySelector('.view-file').style.display = 'none';
        document.querySelector('.divider').style.display = 'none';
    } else {
        // Hides initial empty state and displays the project lists controls
        document.querySelector('.empty-state').style.display = "none";
        document.querySelector('.form-container').style.display = 'block';
        document.querySelector('.board').style.display = 'flex';
        document.querySelector('.continue-btn').style.display = 'flex';
        document.querySelector('.reset-list-btn').style.display = 'flex';
        document.querySelector('.explore-btn').style.display = 'none';
        document.querySelector('.download-file').style.display = 'flex';
        document.querySelector('.view-file').style.display = 'flex';
        document.querySelector('.board').innerHTML = '';
        
        tempArr.forEach(element => {
            const displaySku = element.variantSku || element.productSku ;
            const displayId = element.variantId || element.productId;
            const newCard = document.createElement('div');
            
            newCard.dataset.id = element.productId;
            newCard.dataset.variantId = element.variantId;
            newCard.className = 'product-card';
            newCard.innerHTML =  `
                <div class="card-wrapper">
                    <div class="card-container"> 
                        <div class="card__item one-half">
                            <a href="${element.productUrl}"><img class="product-image" src="${element.productImage}"></a>
                        </div>
                        <div class="card__item one-half-right">
                            <h4 class="product-heading">
                                <a href="${element.productUrl}">${element.productTitle}</a>
                            </h4>
                            <p class="product-sku">Part Number: ${displaySku.replace(/\(.*\)/g, '').trim()}</p>
                            ${ element.variantTitle ? `<p class = product-part-name>Part Name: ${element.variantTitle}</p>` : ''} 
                            <div class="quantity-wrapper">
                                <div class="quantity-group">
                                    <p class="quantity-label">Quantity</p>
                                    <div class="quantity-container">
                                        <button class="decrement-btn">-</button>
                                            <input name="number" type="number" min="1" value="${element.variantQuantity}" class="quantity-counter"></input>
                                        <button class="increment-btn">+</button>
                                    </div>
                                </div>
                                <div class="length-group">
                                    ${element.hasLength === 'true' ?
                                        ` <p class="product-length-label">Length per Metre</p>
                                        <div class="length-field">
                                            <button class="length-decrement-btn">-</button>
                                                <input name="number" type="number" min="1" value="${element.variantLength}" class="length-counter"></input>
                                            <button class="length-increment-btn">+</button>
                                        </div>` : ''}
                                </div>
                            </div>   
                        </div>
                        <div class="delete-container">
                            <button class="delete-btn" data-id="${displayId}" aria-label="delete-product">x</button>
                        </div> 
                    </div>
                </div>
            `
            document.querySelector('.board').appendChild(newCard);
            
            const cardLengthEdit = newCard.querySelector(".length-counter");
            const quantityLengthEdit = newCard.querySelector(".quantity-counter");
            
            // Finds the saved product variant and updates its length in localStorage
            if (cardLengthEdit) {
                cardLengthEdit.addEventListener("input", function(e) {
                    let projectItems = checkStorage();
                    const found = projectItems.find(p => p.variantId === element.variantId);
                    
                    if (found) {
                        found.variantLength = e.target.value;
                    }
                    localStorage.setItem('projectItems', JSON.stringify(projectItems));
                    updateCounter(projectItems);
                });
            }

            // Validates the edited quantity and updates the stored project-list value.
            if (quantityLengthEdit) {   // TO DO: Review Code
                quantityLengthEdit.addEventListener("input", function(e) {
                    checkInput(e.target, 'quantity-counter');
                });
            }
        });

    }
}

// Initialises the project list board and counter
// Handles interactions on product cards
if (document.querySelector('.board')) {
    renderBoard();
    let board = document.querySelector('.board');
    const projectItems = checkStorage();
    updateCounter(projectItems);

    board.addEventListener("click", function(e) {
        const card = e.target.closest('.product-card');
        if (!card) return;

        const productId = card.dataset.id;
        const counter = card.querySelector('.quantity-counter');
        const currentValue = parseInt(counter.innerText);
        const projectItems = checkStorage();
        const found = projectItems.find(p => p.productId === card.dataset.id);
        
        // Handles interactions with the quantity buttons on a product card and updates counter
        if (e.target.classList.contains('decrement-btn')) {
            counter.stepDown();
        } 

        if (e.target.classList.contains('increment-btn')) {
            counter.stepUp();
        }
        
        if (found) {
            found.variantQuantity = counter.value;
        }
            
        // Handles interactions with the length buttons on a product card and updates counter
        const lengthCounter = card.querySelector('.length-counter');
        if (lengthCounter) {
            if (e.target.classList.contains('length-decrement-btn')) {
                lengthCounter.stepDown();
            } 

            if (e.target.classList.contains('length-increment-btn')) {
                lengthCounter.stepUp();
            }
            
            if (found) {
                found.variantLength = lengthCounter.value;
            }
        }
            
        localStorage.setItem('projectItems', JSON.stringify(projectItems));
        updateCounter(projectItems);
    })

    let deleteFromProjectBtn = document.querySelector('.board');                      

    // Removes a product from the project list and updates the counter
    deleteFromProjectBtn.addEventListener("click", function(e) {
        if (e.target.classList.contains('delete-btn')) {
            let projectItems = checkStorage();
            projectItems = projectItems.filter((item) => (item.variantId || item.productId) !== e.target.dataset.id);
            localStorage.setItem('projectItems', JSON.stringify(projectItems)); // Save updated array into local storage
            updateCounter(projectItems);
            renderBoard();
        }
    });
}

let resetList = document.querySelector('.reset-list-btn');

// Clears the project list page and updates local storage
// Updates the project button counter after the list is cleared
if (resetList) {    // TO DO: Review code
    resetList.addEventListener("click", function(e) {
        let projectItems = checkStorage();
        projectItems = projectItems.filter((item) => item.removeItem );
        localStorage.setItem('projectItems', JSON.stringify(projectItems)); // Save updated array into local storage
        updateCounter(projectItems);
        renderBoard();
    })
}


const exists = document.querySelector('.project-button-counter');

// Retrieves project list and updates project button counter
if (exists) {
    const projectItems = checkStorage();
    updateCounter(projectItems); 
}

// Retrieves markup led information in products'product details and returns matching row  
// Displays LED details in generatePDF()
function getIncludedLed(ledInformation, variantSku) {

    const temp = document.createElement('div');
    temp.innerHTML = ledInformation;   
    const rows = temp.querySelectorAll('tr'); 
    if (rows.length === 0) { 
        return ''; 
    }

    const headerRow = rows[0]; 
    const headerCell = headerRow.querySelectorAll('td');
    
    // Retrieves appropriate markup string and stores cell's position number
    // Iterated by column
    let ledColIndex = -1; // Placeholder
    headerCell.forEach((cell, index) => {   
        if (cell.textContent.trim() === 'Included LED') { 
            ledColIndex = index;     
        }
    })

    if (ledColIndex === -1) {
        console.warn("Included LED column not found");
        return '';
    }

    // Searches each table row for the matching variant SKU and retrieves its LED information
    // Skips the header row (by starting at 1) when searching for the variant
    for (let i =1; i < rows.length; i++) { 
        const row = rows[i]; 
        const rowCells = row.querySelectorAll('td');   
        const rowSku = rowCells[0].textContent.replace(/\\n/g, "").trim();

        console.log("TABLE SKU:", JSON.stringify(rowCells[0].textContent));
        console.log("VARIANT SKU:", JSON.stringify(variantSku));

        if ( rowSku === variantSku.trim) {     
            // Gets the LED information from the column containing the matching data
            const ledCell = rowCells[ledColIndex];  
                if (!ledCell) {
                    // Stop running if the expected LED information is missing from matching row
                    console.warn(
                        "LED cell missing",
                        { variantSku, ledColIndex, rowCells }
                    );
                return '';
            }

            // Removes excess tags and new lines from LED information markup
            const ledHtml = ledCell.innerHTML
                            .replace(/<br\s*\/?>/gi, ' ') 
                            .replace(/\s+/g, ' ')
                            .trim(); 

            // Strips any remaining HTML syntax
            const ledText = ledHtml
                            .replace(/<[^>]*>/g, '')        
                            .replace(/&nbsp;/gi, ' ')
                            .trim();

            return ledText;     
        }
    }
    // Displays nothing if no matching row was found
    return ''; 
}

// Builds a PDF with products added to the project list, using their saved project data
async function generatePDF() {
    
    // Extracts jsPDF from window jsPDF
    const { jsPDF } = window.jspdf; 

    // Creates a new document and compresses it
    const doc = new jsPDF({
        compress: true 
    });  

    // Passes doc as an argument to create margins
    const m = createMargins(doc);  
    const projectItems = checkStorage();

    // Gets document's width and height size 
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();

    doc.setFillColor(255);
    doc.rect(0, 0, width, height, "F");

    doc.setFillColor(0);
    doc.rect(0, 0, width, 30, "F");

    // Waits for previous 
    // Fetches logo image 
    const response = await fetch ("https://cdn.shopify.com/s/files/1/0080/5067/1734/files/HCP-LOGO-WHITE.png?v=1779333273"); 
    
    // Converts fetched image into a Blob
    // Blob is binary data that represents the image
    const blob = await response.blob();  
    const base64 = await convertToBase64(blob);
    doc.addImage(base64, 'PNG', m.left, 8, 23, 15);

    const projInfo = document.querySelector('#project-name').value;
    const nameInfo = document.querySelector('#name').value;
    const phoneInfo = document.querySelector('#phone').value;
    const emailInfo = document.querySelector('#email').value;
    
    // Positions document name on the right, relative to the margins
    doc.setTextColor(255);
    doc.setFontSize(15)
    doc.setFont('helvetica', 'bold');
    doc.text("Project List", m.right, 15 , {align: "right"});
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12)
    doc.text(projInfo, m.right, 22 , {align: "right"});

    // Waits for all operations related to images to finish before continuing
    const productImage = await Promise.all(

        /* Processes all images of project items in the local storage
            Turns images into binary data and converts them into Base64 data so 
            it can be used when generating the document
        */
        projectItems.map(async (item) => {
            const photo = await fetch(item.productImage); 
            const photoBlob = await photo.blob();  
            const productPhoto = await convertToBase64(photoBlob); // Turns into base 64
            return productPhoto;
        })
    )

    doc.setTextColor(0);
    doc.setFontSize(13);
    doc.setFont("helvetica", 'bold');
    doc.text("Summary", m.left, 43,{ align: 'left'}); 

    // Positions information for x coordinate
    const infoX = m.left + 35;  
    const rows = setLineHeight();
    
    doc.setTextColor(0);
    doc.setFont("helvetica", 'bold');
    doc.setFontSize(10);
    
    // Stores the input value of users'details relative to margin
    doc.text("Project Name:", m.left, rows[0], {align: "left"});
    doc.text("Name:",  m.left, rows[1], {align: "left"});
    doc.text("Phone Number:", m.left, rows[2], {align: "left"});
    doc.text("Email:", (m.left + 100), rows[0], {align: "left"});             

    doc.setFont("helvetica", 'normal');
    doc.text(projInfo, infoX, rows[0], {align: "left"});
    doc.text(nameInfo, infoX, rows[1], {align: "left"});
    doc.text(phoneInfo, infoX, rows[2], {align: "left"});
    doc.text(emailInfo, (m.left + 135), rows[0], {align: "left"});

    // Displays the current time and date when the PDF is generated
    doc.setFont('helvetica', 'normal');
    const date = new Date();
    let day = date.getDate();
    let month = date.getMonth() + 1;
    let year = date.getFullYear();

    let hours = date.getHours();
    let min = date.getMinutes();
    let sec = date.getSeconds().toString().padStart(2, '0');

    doc.setFont("helvetica", 'bold');
    doc.text("Generated:", (m.left + 100), rows[1], {align: 'left'});
    doc.setFont("helvetica", 'normal');
    doc.text(`${day}/${month}/${year} ${hours}:${min}:${sec}`, (m.left + 135), rows[1], {align: 'left'});

    // Displays project items'saved project data as information in a table
    doc.autoTable({
        startY: 73, 
        head: [["Product", "SKU", "Description", "Quantity"]],
        body: projectItems.map((item) => {
            let ledSku = item.variantSku || item.productSku;
            let ledString = getIncludedLed(item.ledDetails, ledSku);
            const regexCheck = /-(\d+)M$/;
            const lengthMatch = (item.variantSku || "").match(regexCheck);
            let rollLength = "";

            // Extracts the product's length from its SKU and appends the unit to matched case
            if (lengthMatch) {
                rollLength = lengthMatch[1]; 
                rollLength += ' metre';  
            }

            // Displays a product's quantity and specified length when product's sku matches string case 
            const normalDisplay = `${item.variantQuantity? item.variantQuantity +'x' : ""}\n${
                item.hasLength === 'true' ? item.variantLength + ' metre' : ""
            }`;

            let finalQuantityDisplay = normalDisplay;
            if (rollLength.trim()) {
                finalQuantityDisplay = `${item.variantQuantity + 'x'}\n${rollLength}`;
            }

            return [
                "", // didDrawCell handles image column
                `${item.variantTitle? item.variantTitle + ":" : ""}\n${(item.variantSku || item.productSku || "")
                    // Removes excess characters and adds LED information on a newline
                    .replace(/\(.*?\)/g, "")}${ledString? '\nIncluded LED: ' + ledString : ''}`,

                // Strips information of remaining tags, whitespaces, specific strings and collapses two or more consecutive newlines into one.
                item.productDescription
                    .replace(/<[^>]*>/g, "")
                    .trim()
                    .replace(/Maximum length.*\n?/gi, "")
                    .trim()
                    .replace(/\n{2,}/g, '\n')
                    .replace(/Supplied in.*\n?/gi, "")
                    .replace(/Up to \d+m.*\n?/gi, "")
                    .replace(/Custom Cuts.*\n?/gi, ""),
                finalQuantityDisplay,
            ]
        }),
        margin: { bottom: 60, left: m.left, right: m.left },
        theme: 'plain',
        headStyles: {
            textColor: 245,
            fillColor: 0,
            halign: 'left',
            fontSize: 10
        },
        bodyStyles: {lineWidth: 0, minCellHeight: 50, fontWeight:'bold', fontSize: 10, valign: 'middle'},
        tableWidth: 'auto',
        columnStyles: {
            0: { cellWidth: 38},
            2: { cellWidth: 70},
            3: { halign: 'center'}
        },

        didDrawCell: function(cell) {
            // Draws cell border 
            doc.setDrawColor(200);
            doc.setLineWidth(0.3);
            doc.line(
                cell.cell.x,
                cell.cell.y + cell.cell.height,
                cell.cell.x  + cell.cell.width,
                cell.cell.y + cell.cell.height
            );

            // Draws product image 
            if (cell.row.section === 'body' && cell.column.index === 0) {
                const image = productImage[cell.row.index];
                if (!image) return ;
                doc.addImage(
                    image,
                    'UNKNOWN',
                    cell.cell.x + (cell.cell.width/2) - 15,
                    cell.cell.y + (cell.cell.height/2) - 15,
                    30, 
                    30 
                );
            }  
        },
        
    });

    // Initalises QR Reseller Image
    const resellerLocator = await fetch ("https://cdn.shopify.com/s/files/1/0080/5067/1734/files/HCP_Store_Locator.png?v=1781504707"); // Fetches logo URL path
    const resellerBlob = await resellerLocator.blob();
    const storeImg = await convertToBase64(resellerBlob);
    const pageCount = doc.getNumberOfPages();
    const rowsY = setFooterLineHeight();

    // Displays company's details at the bottom of each page
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        doc.setTextColor(0);
        doc.setFontSize(10);
        doc.text("Havit Commercial", m.left, rowsY[0], {align: "left"});
        doc.text("U8/133 Beauchamp Road, Matraville. NSW 2036 Australia", m.left, rowsY[1], {align: "left"});
        doc.text("+61 (02) 9381 8383", m.left, rowsY[2], {align: "left"});
        doc.text("sales@havitcommercial.com.au", m.left, rowsY[3], {align: "left"});

        doc.addImage(storeImg, 'PNG', (m.right - 23), 265, 23, 23); // Positions QR Code
        doc.setFontSize(10);
        doc.text("Scan for Reseller Locator", 163, 275, { align: 'center', maxWidth: 15 }); 
    }

    var fileName = projInfo + "-Commercial-Project-List.pdf";

    doc.setProperties({ 
        title: fileName,
        author: 'Havit Commercial',
        subject: 'Havit Commercial', 
        creator: 'Havit Commercial'
    }); 

    // Returns the document with its information and its name
    return {
        doc: doc,
        fileName: fileName
    };
}

// TO DO: Add comments
function createDownloadAnchor(blob, fileName) {
    var pdfUrl = URL.createObjectURL(blob);
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) { 
        window.open('','_blank');
    }

    var anchor = document.createElement('a');
    anchor.href = pdfUrl;
    anchor.download = fileName;
    anchor.setAttribute('download', fileName);
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();

    setTimeout(function () {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(pdfUrl);
    }, 700);
}

/* Opens the generated PDF in a new preview window and
    creates the preview layout. 
    Preview layout includes the PDF viewer and download option.
*/
function openPreview(blob, fileName) {

    // Creates a temporary browser URL for the PDF blob
    var browserUrl = URL.createObjectURL(blob);
    var previewWindow = window.open('', '_blank');

    // Checks whether the user is on a mobile phonw
    // TO DO: Review Code
    var isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Stops preview of PDF if the browser blocks the new window
    if (!previewWindow) {  
        alert('Please allow pop-ups to preview the PDF');
        return;
    }

    // Creates the PDF preview window and its layout
    previewWindow.document.title = fileName;
    var preview = previewWindow.document.createElement('iframe');
    var previewBar = previewWindow.document.createElement('div');
    previewBar.className = 'project-tool-preview-bar';
    
    var previewName = previewWindow.document.createElement('div');
    previewName.className = 'project-tool-preview-name';

    // Creates a download link for the generated PDF
    var downloadFile = previewWindow.document.createElement('a');
    downloadFile.className = 'project-tool-download-file';
    downloadFile.href = browserUrl;
    downloadFile.download = fileName;
    downloadFile.setAttribute('download', fileName);
    downloadFile.textContent = 'Download PDF';

    // Creates the PDF viewer inside the preview window
    var iframe = previewWindow.document.createElement('iframe');
    iframe.src = browserUrl + '#toolbar=0&navpanes=0&scrollbar=1';
    iframe.title = fileName;

    // Creates custom styling for the preview window and toolbar
    var customStyle = previewWindow.document.createElement('style');
    customStyle.textContent =
    'html,body{margin:0;height:100%;background:#1f1f1f;font-family:Arial,sans-serif;overflow:hidden;}' +
    '.project-tool-preview-bar{height:54px;background:#030303;color:#fff;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:0 18px;box-sizing:border-box;border-bottom:1px solid #333;}' +
    '.project-tool-preview-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px;font-weight:700;}' +
    '.project-tool-download-file{background:#fff;color:#000;text-decoration:none;font-size:12px;font-weight:800;padding:10px 16px;text-transform:uppercase;letter-spacing:.08em;white-space:nowrap;}' +
    'iframe{width:100%;height:calc(100% - 54px);border:0;background:#fff;display:block;}';

    // Adds the custom styling and preview elements to the new window.
    previewWindow.document.head.appendChild(customStyle);
    previewBar.appendChild(previewName);
    previewBar.appendChild(downloadFile);
    previewWindow.document.body.appendChild(previewBar);
    previewWindow.document.body.appendChild(iframe);
}

// Opens PDF in a new preview window after PDF is generated
async function viewPDF() {
    var built = await generatePDF();
    if (!built) return; 

    var blob = built.doc.output('blob');
    openPreview(blob, built.fileName);
}

// Downloads PDF in a new preview window after PDF is generated
async function downloadPDF() {
    var built = await generatePDF();
    if (!built) return;

    var blob = built.doc.output('blob');
    createDownloadAnchor(blob, built.fileName); 
}

const downloadBtn = document.querySelector('.download-file');
const form = document.querySelector('.user-details');

// Downloads PDF when form is completed and meets criterion
// Displays error(s) if form is completed incorrectly
if (downloadBtn) {
    downloadBtn.addEventListener("click", function() {
        const phoneValue = document.getElementById('phone').value;
        const emailValue = document.getElementById('email').value;
        if (form.checkValidity() && checkPhoneInput(phoneValue) && checkEmailInput(emailValue)) {
            downloadPDF();
        } else {
            form.reportValidity();
            return;
        }
    })
}

// TO DO: Check view button bug 
// Shows PDF when form is completed and meets criterion
// Displays error(s) if form is completed incorrectly
const viewBtn = document.querySelector('.view-file');
if (viewBtn) {
    viewBtn.addEventListener("click", function() {
        const phoneValue = document.getElementById('phone').value;
        const emailValue = document.getElementById('email').value;
        if (form.checkValidity() && checkPhoneInput(phoneValue) && checkEmailInput(emailValue)) {
           viewPDF();
        } else {
            form.reportValidity();
            return;
        }
    })
} 

// Used by liquid counterpart in the form
// Raises errors when incorrect input type is used for phone and email
function checkPhoneInput(value) {
    const errorElement = document.getElementById("phone-exception");
    const convertedNumber = Number(value);

    if (Number.isNaN(convertedNumber)) {
        errorElement.textContent = "Please enter a valid phone number." ;
        return false;
    } else {
        errorElement.textContent = '';
        return true;
    }  
}

const phoneField = document.getElementById("phone");

if (phoneField) {
    phoneField.addEventListener("input", function(e) {
        const target = e.currentTarget.value;
        checkPhoneInput(target);
    })
}

function checkEmailInput(value) {
    const errorElement = document.getElementById("email-exception");
    const emailPattern = /^[^\s@]+@[^\s@]+\.(com|com\.au)$/;

    if (!emailPattern.test(value)) {
        errorElement.textContent = "Please enter a valid email";
        return false;
    } else {
        errorElement.textContent = '';
        return true;
    }  
}

const emailField = document.getElementById("email");
if (emailField) {
    emailField.addEventListener("input", function(e) {
        const target = e.currentTarget.value;
        checkEmailInput(target);
    })
}
